import {Tree} from 'antd'
import classNames from 'classnames'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {LoginContext} from 'components/LoginContext'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import React, {useContext, useMemo} from 'react'
import {RiArrowRightCircleFill, RiBookmark3Line} from 'react-icons/ri'
import {NullAsDefault, renderIf} from 'views/Shared'
import {CodiActions, getRowTitle} from './codiHelpers'
import CodiContext from './CodiContext'

const IndexTitle = ({
                        item,
                        count,
                        active,
                        root = false,
                        prefix = null,
                        suffix = null,
                    }) =>
    renderIf(
        item,
        <Translate>
            {(t) => (
                <span
                    className={classNames(
                        'space-x-1',
                        active ? 'font-bold text-primary' : 'font-medium text-color-100'
                    )}>
          {prefix}
                    <span
                        className={classNames(
                            'flex-1',
                            root ? 'font-bold text-lg text-color-000' : 'text-sm'
                        )}>
            {getRowTitle({item, translate: t})}
          </span>
                    {renderIf(
                        _.get(item, 'page'),
                        <span className="font-normal text-xs text-color-400 italic leading-tight text-right flex-1">
              {`(${t('page')} - ${_.get(item, 'page')})`}
            </span>
                    )}
                    {suffix}
        </span>
            )}
        </Translate>
    )

const createIndexes = (data, params) => {
    const {
        hash,
        level = 0,
        parent = [],
        selected_row_id,
        isBookmark = NullAsDefault,
    } = params ?? {}
    return Array.from(data ?? []).map((item) => {
        const {id, idname, content, index_cd, index_typecd, codi_document_page} =
        item ?? {}
        const children_rows = Array.from(item?.children_rows ?? [])
        const next_parent = [...(parent ?? []), id]
        const children = createIndexes(
            children_rows.filter((row) => !row?.is_html),
            {
                ...(params ?? {}),
                level: level + 1,
                parent: next_parent,
            }
        )
        const active = [selected_row_id].includes(id)
        return {
            key: id,
            children,
            data: {
                id,
                idname,
                content,
                index_cd,
                index_typecd,
                codi_document_page,
            },
            icon:
                hash && hash === item?.id ? (
                    <RiArrowRightCircleFill
                        size={14}
                        className="text-blue-500"
                    />
                ) : undefined,
            parent: next_parent,
            isLeaf: _.isEmpty(children_rows),
            keywords: [content, index_cd, index_typecd],
            title: (
                <IndexTitle
                    item={item}
                    hash={hash}
                    active={active}
                    root={level === 0}
                    count={children.length}
                    suffix={
                        <LoginContext.Consumer>
                            {(login) =>
                                renderIf(
                                    login && isBookmark(item),
                                    <RiBookmark3Line
                                        size={13}
                                        className="text-orange-500"
                                    />
                                )
                            }
                        </LoginContext.Consumer>
                    }
                />
            ),
        }
    })
}

const CodiIndexes = () => {
    const {setOpenMenu = NullAsDefault} = useContext(LayoutContext)
    const {
        rows,
        selected_row_id,
        bookmarkManager,
        setCurrent = NullAsDefault,
        handleAction = NullAsDefault,
    } = useContext(CodiContext)

    const {isBookmark} = bookmarkManager ?? {}

    const indexes = useMemo(() => Array.from(rows ?? []), [rows])

    const treeData = useMemo(() => {
        const hash = window.location.hash
        return Array.from(
            createIndexes(indexes, {
                isBookmark,
                selected_row_id,
                hash: _.isEmpty(hash) ? undefined : hash.slice(1),
            }) ?? []
        )
    }, [indexes, isBookmark, selected_row_id])

    const onDismiss = () => {
        setCurrent(null)
        setOpenMenu(false)
    }

    const handleSelect = (keys, info) => {
        const {key, data, children, expanded} = info?.node ?? {}
        if (children?.length > 0 && !expanded) {
            return
        }
        const page_id = data?.codi_document_page?.id
        const ref_detail_id = data?.codi_document_page?.ref_id

        handleAction(CodiActions.SELECT_ROW, {
            id: page_id ?? key,
            detail_id: page_id ? key : undefined,
            ref_detail_id: ref_detail_id ? key : undefined,
        })
        onDismiss()
    }

    const defaultExpandedKeys = useMemo(() => {
        if (treeData.length > 1) {
            return []
        }
        const root_nodes = treeData.map(({key}) => key)
        return _.uniq([selected_row_id, ...root_nodes])
    }, [treeData, selected_row_id])

    return (
        <div className="flex flex-col p-2 gap-2">
            <Tree
                showIcon={true}
                treeData={treeData}
                expandAction="click"
                onSelect={handleSelect}
                autoExpandParent={true}
                defaultExpandedKeys={defaultExpandedKeys}
                className="less-space custom-tree-indexes"
            />
        </div>
    )
}

export default CodiIndexes
